import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { tap, filter, take, map, withLatestFrom, switchMap, catchError } from 'rxjs/operators';
import {
  LoadNextWorkingDayAction,
  LoadNextWorkingDayActionV5,
  LoadNextWorkingDayCompleteAction,
  NextWorkingDayActionTypes,
} from '../actions/next-working-day.actions';
import { Store, createSelector, Action } from '@ngrx/store';
import * as moment from 'moment';
import { getJobsiteSelected } from '../../../../modules/review/reducers/review.selectors';
import { LOAD_CONFIG_COMPLETE } from '../../../../modules/specification/actions/specification.actions';
import { RetailSpecificationActionTypes } from '../../../../modules/retail-specification/actions/retail-specifications.actions';
import { getSelectedExperience, OrderBuilderState } from '../../../../reducers';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { NextWorkingDayService, NextWorkingDayRequest } from '@cemex-core/cmx-api';
import { LanguageKey } from '@ordersnproducts/modules/shared/constants/language-key';
import { NextWorkingDayResponseV5 } from '@cemex-core/cmx-api/public_api';
import { getSelectedExperienceModify } from '@ordersnproducts/modules/new-modify-order/modules/new-modify-product-specification/selectors';

const nextWorkingDayFormats = {
  [LanguageKey.English]: 'YYYYMMDD',
  [LanguageKey.Spanish]: 'YYYYMMDD',
};

const getRequestData = createSelector(getJobsiteSelected, getLegalEntity, (jobsite, customer) => {
  return { jobsite, customer };
});
@Injectable()
export class NextWorkingDayEffect {
  loadConfig: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LOAD_CONFIG_COMPLETE, RetailSpecificationActionTypes.INIT),
        tap((action) => {
          this.store
            .select(getRequestData)
            .pipe(
              filter(({ jobsite }) => jobsite !== null),
              take(1),
              map(({ jobsite, customer }) => {
                return {
                  countryCode: customer.countryCode,
                  shipmentLocationCode: jobsite.shipmentLocationCode,
                  languageKey: LanguageKey.Spanish,
                };
              }),
            )
            .subscribe((request: NextWorkingDayRequest) => this.store.dispatch(new LoadNextWorkingDayAction(request)));
        }),
      ),
    { dispatch: false },
  );

  loadNextWorkingDay: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NextWorkingDayActionTypes.LOAD),
        tap((action: LoadNextWorkingDayAction) => {
          this.nextWorkingDayService.getNextWorkingDay(action.payload).subscribe((result) => {
            this.store.dispatch(
              new LoadNextWorkingDayCompleteAction({
                date: moment(result.dateAvailable, nextWorkingDayFormats[action.payload.languageKey]).toDate(),
              }),
            );
          });
        }),
      ),
    { dispatch: false },
  );

  loadNextWorkingDayV5: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NextWorkingDayActionTypes.LOAD_CUT_OF_TIME),
        switchMap((action: LoadNextWorkingDayActionV5) => {
          return this.store.select(getSelectedExperience).pipe(
            filter((experience) => !!experience),
            map((experience) => {
              return [action, experience];
            }),
            take(2),
          );
        }),
        switchMap(([action, experienceState]: [LoadNextWorkingDayActionV5, any]) => {
          return this.nextWorkingDayService.getNextWorkingDayV5(action.payload).pipe(
            map((result: NextWorkingDayResponseV5) => {
              if (experienceState.experience && experienceState.experience.experiences[0].isOnline === 1) {
                return this.store.dispatch(
                  new LoadNextWorkingDayCompleteAction({
                    date: moment(result.dateAvailable, nextWorkingDayFormats[LanguageKey.English]).toDate(),
                    blackout: result['blackout'],
                    timeCut: result.timeCut,
                    // @ts-ignore
                    timezone: result.timeZone,
                  }),
                );
              } else {
                return this.store.dispatch(
                  new LoadNextWorkingDayCompleteAction({
                    date: moment(new Date()).toDate(),
                    blackout: result['blackout'],
                    timeCut: result.timeCut,
                    // @ts-ignore
                    timezone: result.timeZone,
                  }),
                );
              }
            }),
            catchError((error) => {
              return of(null);
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  loadNextWorkingDayModifyV5: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NextWorkingDayActionTypes.LOAD_CUT_OF_TIME_MODIFY),
        withLatestFrom(this.store.select(getSelectedExperienceModify)),
        switchMap(([action, experienceState]: [LoadNextWorkingDayActionV5, any]) => {
          return this.nextWorkingDayService.getNextWorkingDayV5(action.payload).pipe(
            map((result: NextWorkingDayResponseV5) => {
              if (experienceState.experience && experienceState.experience.experiences[0].isOnline === 1) {
                return this.store.dispatch(
                  new LoadNextWorkingDayCompleteAction({
                    date: moment(result.dateAvailable, nextWorkingDayFormats[LanguageKey.English]).toDate(),
                    blackout: result['blackout'],
                  }),
                );
              } else {
                return this.store.dispatch(new LoadNextWorkingDayCompleteAction({ date: moment(new Date()).toDate() }));
              }
            }),
            catchError((error) => {
              return of(null);
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private nextWorkingDayService: NextWorkingDayService,
  ) {}
}
